/*
  Bangle buy box.
  - bangle-tiers: offer cards (Buy One / Buy 2 get 1 free / ...). The selected card expands to show one
    row per set with Size and Colour selects. Row 1 drives the main (visually hidden) variant picker so
    price, images and URL keep updating through Dawn's machinery; untouched rows follow row 1.
  - Direct checkout: the product form clears the cart, adds one line per set and sends the customer
    straight to checkout. No cart drawer.
  - bangle-countdown, bangle-delivery, bangle-gallery-dots: helpers.
*/

(() => {
  const group = (whole, sep) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  window.bangleMoney = (cents, format) => {
    const amount = (Math.round(cents) / 100).toFixed(2);
    const [whole, frac] = amount.split('.');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/g, `${group(whole, ',')}.${frac}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, group(whole, ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${group(whole, '.')},${frac}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, group(whole, '.'))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, `${group(whole, ' ')},${frac}`)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, `${group(whole, "'")}.${frac}`);
  };
})();

if (!customElements.get('bangle-tiers')) {
  customElements.define(
    'bangle-tiers',
    class BangleTiers extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.form = document.getElementById(`product-form-${this.sectionId}`);
        this.moneyFormat = this.form ? this.form.dataset.moneyFormat : '';
        this.current = this.readCurrent();
        this.syncing = false;

        this.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"]')) {
            this.apply();
          } else if (event.target.matches('select[data-option-index]')) {
            this.onRowChange(event.target);
          }
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId !== this.sectionId) return;
            if (data.variant) {
              this.current = data.variant;
              this.followVariant();
            }
            if (data.html) this.refreshPricing(data.html);
            this.apply();
          });
        }

        this.bindCheckout();
        this.followVariant();
        this.apply();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.onSubmit) document.removeEventListener('submit', this.onSubmit, true);
      }

      /* ---------- data ---------- */

      get variants() {
        if (this._variants) return this._variants;
        const script = document.getElementById(`BangleVariants-${this.sectionId}`);
        try {
          this._variants = script ? JSON.parse(script.textContent) : [];
        } catch (error) {
          this._variants = [];
        }
        return this._variants;
      }

      readCurrent() {
        const script = document.querySelector(`#variant-selects-${this.sectionId} [data-selected-variant]`);
        try {
          return script ? JSON.parse(script.textContent) : null;
        } catch (error) {
          return null;
        }
      }

      get checkedInput() {
        return this.querySelector('input[type="radio"]:checked') || this.querySelector('input[type="radio"]');
      }

      get activeTier() {
        const input = this.checkedInput;
        return input ? input.closest('[data-tier-id]') : null;
      }

      activeRows() {
        const tier = this.activeTier;
        return tier ? [...tier.querySelectorAll('[data-set-index]')] : [];
      }

      rowValues(row) {
        return [...row.querySelectorAll('select[data-option-index]')].map((select) => select.value);
      }

      setRow(row, values) {
        row.querySelectorAll('select[data-option-index]').forEach((select, i) => {
          if (values[i] !== undefined && select.value !== values[i]) select.value = values[i];
        });
      }

      rowVariant(row) {
        const chosen = this.rowValues(row);
        return this.variants.find((variant) => chosen.every((value, i) => variant.options[i] === value)) || null;
      }

      /* ---------- syncing ---------- */

      /* Dawn changed the variant (or page loaded): every row 1, and every untouched row, mirrors it. */
      followVariant() {
        if (!this.current || !this.current.options) return;
        this.querySelectorAll('[data-set-index]').forEach((row) => {
          if (row.dataset.setIndex === '1' || !row.dataset.touched) this.setRow(row, this.current.options);
        });
      }

      /* A select in a set row changed. */
      onRowChange(select) {
        if (this.syncing) return;
        const row = select.closest('[data-set-index]');
        if (!row) return;
        if (row.dataset.setIndex === '1') {
          this.pushToMainPicker(this.rowValues(row));
        } else {
          row.dataset.touched = '1';
          this.apply();
        }
      }

      /* Drive Dawn's variant-selects so price, media and URL update; the variantChange event then flows back. */
      pushToMainPicker(values) {
        const picker = document.getElementById(`variant-selects-${this.sectionId}`);
        if (!picker) {
          this.apply();
          return;
        }
        const fieldsets = picker.querySelectorAll('fieldset');
        let changed = null;
        values.forEach((value, i) => {
          const fieldset = fieldsets[i];
          if (!fieldset) return;
          const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((radio) => radio.value === value);
          if (input && !input.checked) {
            input.checked = true;
            changed = input;
          }
          const select = fieldset.querySelector('select');
          if (select && select.value !== value) {
            select.value = value;
            changed = select;
          }
        });
        if (changed) {
          this.syncing = true;
          changed.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncing = false;
        } else {
          this.apply();
        }
      }

      refreshPricing(html) {
        this.querySelectorAll('[data-tier-id]').forEach((tier) => {
          const fresh = html.querySelector(`[data-tier-id="${tier.dataset.tierId}"] [data-tier-pricing]`);
          const current = tier.querySelector('[data-tier-pricing]');
          if (!fresh || !current) return;
          current.innerHTML = fresh.innerHTML;
          current.dataset.total = fresh.dataset.total || '';
        });
      }

      /* ---------- totals ---------- */

      items() {
        const rows = this.activeRows();
        const main = this.current || this.readCurrent();
        if (!rows.length) {
          return main ? [{ id: main.id, price: main.price, available: main.available !== false }] : [];
        }
        return rows.map((row, index) => {
          const variant = index === 0 && main ? main : this.rowVariant(row) || main;
          return variant ? { id: variant.id, price: variant.price, available: variant.available !== false } : null;
        }).filter(Boolean);
      }

      computeTotal(free) {
        const prices = this.items().map((item) => item.price).sort((a, b) => a - b);
        if (!prices.length) return null;
        return prices.slice(Math.min(free, prices.length - 1)).reduce((sum, price) => sum + price, 0);
      }

      apply() {
        const input = this.checkedInput;
        if (!input) return;
        const quantity = parseInt(input.value, 10) || 1;
        const free = parseInt(input.dataset.free, 10) || 0;

        this.querySelectorAll('[data-tier-id]').forEach((tier) => {
          const selected = tier.contains(input);
          tier.classList.toggle('is-selected', selected);
          const panel = tier.querySelector('[data-tier-sets]');
          if (panel) panel.setAttribute('aria-hidden', String(!selected));
        });

        const pricing = input.closest('[data-tier-id]')?.querySelector('[data-tier-pricing]');
        let total = pricing ? pricing.dataset.total : '';
        const computed = this.computeTotal(free);
        if (computed !== null && this.moneyFormat) total = window.bangleMoney(computed, this.moneyFormat);

        const quantityInput = document.getElementById(`BundleQuantity-${this.sectionId}`);
        if (quantityInput) quantityInput.value = quantity;
        const pill = document.getElementById(`CtaPrice-${this.sectionId}`);
        if (pill && total) pill.textContent = total;

        this.lastDetail = { sectionId: this.sectionId, quantity, free, total, label: input.dataset.tierLabel || '' };
        this.dispatchEvent(new CustomEvent('bangle:tier-change', { bubbles: true, detail: this.lastDetail }));
      }

      /* ---------- checkout ---------- */

      bindCheckout() {
        if (!this.form) return;
        this.onSubmit = (event) => {
          if (event.target !== this.form) return;
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.checkout();
        };
        document.addEventListener('submit', this.onSubmit, true);
      }

      setBusy(busy) {
        const buttons = [this.form.querySelector('[type="submit"]'), ...document.querySelectorAll(`[form="${this.form.id}"]`)];
        buttons.forEach((button) => {
          if (!button) return;
          button.classList.toggle('loading', busy);
          button.toggleAttribute('aria-busy', busy);
          button.toggleAttribute('disabled', busy);
          const spinner = button.querySelector('.loading__spinner');
          if (spinner) spinner.classList.toggle('hidden', !busy);
        });
      }

      showError(message) {
        const wrapper = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
        const text = wrapper ? wrapper.querySelector('.product-form__error-message') : null;
        if (wrapper && text) {
          text.textContent = message;
          wrapper.hidden = false;
        } else {
          window.alert(message);
        }
      }

      async checkout() {
        let items = this.items();
        if (!items.length) {
          const idInput = this.form.querySelector('input[name="id"]');
          if (idInput) items = [{ id: parseInt(idInput.value, 10), available: true }];
        }
        if (items.some((item) => !item.available)) {
          this.showError(this.form.dataset.unavailableText || 'One of the chosen sets is sold out.');
          return;
        }
        const merged = new Map();
        items.forEach((item) => merged.set(item.id, (merged.get(item.id) || 0) + 1));
        const payload = { items: [...merged.entries()].map(([id, qty]) => ({ id, quantity: qty })) };

        const routes = window.routes || {};
        const root = (routes.cart_url || '/cart').replace(/\/cart$/, '');
        const clearUrl = `${root}/cart/clear.js`;
        const addUrl = routes.cart_add_url || `${root}/cart/add.js`;
        const checkoutUrl = this.form.dataset.checkoutUrl || `${root}/checkout`;

        this.setBusy(true);
        try {
          if (this.form.dataset.clearCart !== 'false') {
            await fetch(clearUrl, { method: 'POST', headers: { Accept: 'application/json' } });
          }
          const response = await fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            this.showError(data.description || data.message || this.form.dataset.errorText || 'Could not add to cart.');
            this.setBusy(false);
            return;
          }
          window.location.assign(checkoutUrl);
        } catch (error) {
          this.showError(this.form.dataset.errorText || 'Could not add to cart.');
          this.setBusy(false);
        }
      }
    }
  );
}

if (!customElements.get('bangle-countdown')) {
  customElements.define(
    'bangle-countdown',
    class BangleCountdown extends HTMLElement {
      connectedCallback() {
        this.endTime = parseInt(this.dataset.end, 10) * 1000;
        this.clock = this.querySelector('[data-countdown]');
        if (!this.endTime || !this.clock) return;
        this.tick();
        this.interval = window.setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        window.clearInterval(this.interval);
      }

      tick() {
        const remaining = this.endTime - Date.now();
        if (remaining <= 0) {
          window.clearInterval(this.interval);
          if (!(window.Shopify && window.Shopify.designMode)) {
            this.hidden = true;
            const wrapper = this.closest(".bangle-urgency");
            if (wrapper && !wrapper.querySelector(".bangle-urgency__stock")) wrapper.hidden = true;
          }
          return;
        }
        const total = Math.floor(remaining / 1000);
        const days = Math.floor(total / 86400);
        const pad = (n) => String(n).padStart(2, '0');
        const hms = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
        this.clock.textContent = days > 0 ? `${days}d ${hms}` : hms;
        this.hidden = false;
      }
    }
  );
}

if (!customElements.get('bangle-delivery')) {
  customElements.define(
    'bangle-delivery',
    class BangleDelivery extends HTMLElement {
      connectedCallback() {
        const data = this.dataset;
        const toInt = (value) => parseInt(value, 10) || 0;
        const minDays = toInt(data.dispatchMin) + toInt(data.deliveryMin);
        const maxDays = toInt(data.dispatchMax) + toInt(data.deliveryMax);
        const target = this.querySelector('[data-delivery-text]');
        if (!maxDays || !target) return;

        const addBusinessDays = (days) => {
          const date = new Date();
          let added = 0;
          while (added < days) {
            date.setDate(date.getDate() + 1);
            const day = date.getDay();
            if (day !== 0 && day !== 6) added += 1;
          }
          return date;
        };

        const options = { month: 'short', day: 'numeric' };
        let formatter;
        try {
          formatter = new Intl.DateTimeFormat(data.locale || undefined, options);
        } catch (error) {
          formatter = new Intl.DateTimeFormat(undefined, options);
        }

        const from = formatter.format(addBusinessDays(minDays));
        const to = formatter.format(addBusinessDays(maxDays));

        if (minDays && minDays !== maxDays && data.textRange) {
          target.textContent = data.textRange.replace('[from]', from).replace('[to]', to);
        } else if (data.textSingle) {
          target.textContent = data.textSingle.replace('[to]', to);
        }
      }
    }
  );
}

if (!customElements.get('bangle-gallery-dots')) {
  customElements.define(
    'bangle-gallery-dots',
    class BangleGalleryDots extends HTMLElement {
      connectedCallback() {
        this.viewer = this.closest('slider-component');
        this.gallery = this.closest('media-gallery');

        this.addEventListener('click', (event) => {
          const dot = event.target.closest('[data-target]');
          if (!dot) return;
          if (this.gallery && typeof this.gallery.setActiveMedia === 'function') {
            this.gallery.setActiveMedia(dot.dataset.target, false);
          } else {
            const slide = this.viewer && this.viewer.querySelector(`[data-media-id="${dot.dataset.target}"]`);
            if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft });
          }
        });

        if (this.viewer) {
          this.viewer.addEventListener('slideChanged', (event) => {
            const current = event.detail && event.detail.currentElement;
            if (current) this.setActive(current.dataset.mediaId);
          });
        }

        const active = this.gallery && this.gallery.querySelector('[data-media-id].is-active');
        if (active) this.setActive(active.dataset.mediaId);
      }

      setActive(mediaId) {
        this.querySelectorAll('[data-target]').forEach((dot) => {
          const isActive = dot.dataset.target === mediaId;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
